import React, { useState, useEffect, useRef } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Loading from "./components/Loading";
import AnimatedRoutes from "./components/AnimatedRoutes";
import {
  ALL_KEYS,
  getCriticalKeys,
  getBackgroundKeys,
  fetchDataset,
  getHomeRotatorPreloadUrls,
  preloadImages,
} from "./services/boot";

import "./styles.css";

const emptyLoaded = () =>
  ALL_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

export default function App() {
  const [bootReady, setBootReady] = useState(false);
  const [mainData, setMainData] = useState({
    people: [],
    projects: [],
    publications: [],
    news: [],
    workAreas: [],
    utilities: [],
  });
  const [loaded, setLoaded] = useState(emptyLoaded);
  const retryTimers = useRef([]);

  const mergeData = (key, data) => {
    setMainData((prev) => ({ ...prev, [key]: data }));
    setLoaded((prev) => ({ ...prev, [key]: true }));
  };

  const loadKeySafe = async (key) => {
    try {
      const data = await fetchDataset(key);
      mergeData(key, data);
      return true;
    } catch (error) {
      console.error(`Error loading ${key}:`, error);
      setLoaded((prev) => ({ ...prev, [key]: true }));
      return false;
    }
  };

  const scheduleRetry = (keys, delayMs = 5000) => {
    const timer = setTimeout(() => {
      keys.forEach((key) => {
        loadKeySafe(key);
      });
    }, delayMs);
    retryTimers.current.push(timer);
  };

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      const pathname = window.location.pathname;
      const normalized = pathname.replace(/\/$/, '') || '/';
      const criticalKeys = getCriticalKeys(pathname);
      const backgroundKeys = getBackgroundKeys(criticalKeys);

      const criticalResults = await Promise.all(
        criticalKeys.map(async (key) => {
          try {
            const data = await fetchDataset(key);
            return { key, data, ok: true };
          } catch (error) {
            console.error(`Error loading critical dataset ${key}:`, error);
            return { key, data: [], ok: false };
          }
        })
      );

      if (cancelled) return;

      const criticalData = {};
      const failedCritical = [];
      criticalResults.forEach(({ key, data, ok }) => {
        criticalData[key] = data;
        if (!ok) failedCritical.push(key);
      });

      setMainData((prev) => ({ ...prev, ...criticalData }));
      setLoaded((prev) => ({
        ...prev,
        ...Object.fromEntries(criticalKeys.map((key) => [key, true])),
      }));

      if (normalized === '/') {
        await preloadImages(
          getHomeRotatorPreloadUrls({
            workAreas: criticalData.workAreas || [],
            people: criticalData.people || [],
            projects: criticalData.projects || [],
          })
        );
      }

      if (cancelled) return;
      setBootReady(true);

      backgroundKeys.forEach(async (key) => {
        const ok = await loadKeySafe(key);
        if (!ok) scheduleRetry([key]);
      });

      if (failedCritical.length) {
        scheduleRetry(failedCritical);
      }
    }

    boot();

    return () => {
      cancelled = true;
      retryTimers.current.forEach(clearTimeout);
      retryTimers.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!bootReady) {
    return <Loading />;
  }

  return (
    <div className="App">
      <div className="shape-blob one"></div>
      <div className="shape-blob two"></div>
      <div className="shape-blob three"></div>
      <Navbar />
      <AnimatedRoutes {...mainData} loaded={loaded} />
      <Footer />
    </div>
  );
}
